import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContextIndicator from './ContextIndicator';

const ChatHeader = ({ buttons = [], widgets = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const hasOverflow = scrollWidth > clientWidth;
    
    setShowLeftArrow(hasOverflow && scrollLeft > 1);
    setShowRightArrow(hasOverflow && scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    checkScrollPosition();

    const resizeObserver = new ResizeObserver(checkScrollPosition);
    resizeObserver.observe(container);

    container.addEventListener('scroll', checkScrollPosition);

    return () => {
      resizeObserver.disconnect();
      container.removeEventListener('scroll', checkScrollPosition);
    };
  }, [checkScrollPosition, buttons, widgets]);

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = container.clientWidth * 0.6;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  const arrowButtonStyles = {
    minWidth: 32,
    width: 32,
    height: 32,
    padding: 0,
    backgroundColor: theme.palette.mode === 'dark' 
      ? `${theme.palette.primary.main}22` 
      : `${theme.palette.primary.main}15`,
    border: `1px solid ${theme.palette.primary.main}40`,
    borderRadius: 1,
    flexShrink: 0,
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark'
        ? `${theme.palette.primary.main}35`
        : `${theme.palette.primary.main}25`,
      borderColor: theme.palette.primary.main,
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        minHeight: 40,
        width: '100%',
        mb: 0.5,
        flexShrink: 0,
        gap: 0.5,
      }}
    >
      {/* Left Arrow - Desktop Only */}
      {!isMobile && showLeftArrow && (
        <IconButton
          onClick={() => scroll('left')}
          sx={arrowButtonStyles}
          size="small"
          aria-label="Scroll left"
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      )}

      {/* Scrollable Content Container */}
      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexGrow: 1,
          minWidth: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {/* Left Side: Context Indicator + Widgets */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexShrink: 0,
          }}
        >
          <ContextIndicator />
          {widgets}
        </Box>

        {/* Spacer with Divider */}
        <Box sx={{ flexGrow: 1, minWidth: 8 }} />
        
        {/* Divider */}
        {widgets && buttons.length > 0 && (
          <Box
            sx={{
              width: 1.5,
              height: 24,
              borderRadius: 1,
              background: `linear-gradient(180deg, 
                transparent 0%, 
                ${theme.palette.primary.main}60 20%, 
                ${theme.palette.primary.main}60 80%, 
                transparent 100%)`,
              flexShrink: 0,
              mx: 0.5,
            }}
          />
        )}

        {/* Right Side: Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          {buttons.map((button, index) => (
            <React.Fragment key={button.key || index}>
              {button}
            </React.Fragment>
          ))}
        </Box>
      </Box>

      {/* Right Arrow - Desktop Only */}
      {!isMobile && showRightArrow && (
        <IconButton
          onClick={() => scroll('right')}
          sx={arrowButtonStyles}
          size="small"
          aria-label="Scroll right"
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

ChatHeader.propTypes = {
  buttons: PropTypes.arrayOf(PropTypes.node),
  widgets: PropTypes.node,
};

export default ChatHeader;